import Link from '@/components/Link/Link';
import Loading from '@/components/Loading/Loading';
import { Invoice, PaymentMethod } from '@/hooks/invoice/types';
import { useInvoice } from '@/hooks/invoice/useInvoice';
import { usePaymentSession } from '@/hooks/payment/usePaymentSession';
import { useSwishPayment } from '@/hooks/payment/useSwishPayment';
import { FetchError } from '@/hooks/useApi';
import { payCallbackPath } from '@/router/Routes';
import { failedPaymentStatuses, pendingPaymentStatuses } from '@/utils/constants';
import { deviceType } from '@/utils/device';
import { formatPriceString, formatStringToAmountInCents } from '@/utils/numbers';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PartialPayment from '../components/PartialPayment';
import PaymentOptionsSelector from '../components/PaymentOptionSelector';
import ReferenceDetails from '../components/ReferenceDetails';
import StripePayment from '../components/StripePayment';
import Swish from '../components/Swish';
import { createPartialPaymentSchema, referenceCodeSchema } from '../schemas';

const PaySession = () => {
  const { referenceCode: referenceCodeFromParams } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    invoice,
    bankTransferPaymentId,
    resetInvoice,
    queryError,
    setInvoiceReferenceCode,
    refetchInvoice,
    minimumPaymentAmount
  } = useInvoice();
  const {
    postPaymentSession,
    cancelPaymentSession,
    paymentSession,
    resetPaymentSession,
    setPaymentSessionId,
    partialPayment
  } = usePaymentSession();
  const { fetchQrCode, getSwishAppUrl } = useSwishPayment();
  const [partialPaymentError, setPartialPaymentError] = useState<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);
  const [showQRCode, setShowQRCode] = useState<boolean>(false);
  const [qrCodeBlob, setQrCodeBlob] = useState<string | null>(null);
  const [swishToken, setSwishToken] = useState<string | null>(null);
  const [swishRedirection, setSwishRedirection] = useState<string | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [stripePaymentSessionId, setStripePaymentSessionId] = useState<string | null>(null);
  const [stripeModalOpen, setStripeModalOpen] = useState(false);
  const isValidPartialPayment = !partialPaymentError && !!partialPayment.amount;
  const swishPaymentMethodId = invoice?.paymentMethods.find(
    (method) =>
      method.serviceProvider.toLowerCase() === 'swish' || method.serviceProvider.toLowerCase().includes('swish')
  )?.paymentMethodId;
  const isSwishPayment = selectedPaymentMethod?.paymentMethodId === swishPaymentMethodId;
  const isStripePayment = selectedPaymentMethod?.serviceProvider.toLowerCase() === 'stripe';

  const isValidReferenceCode = (referenceCode: string) => referenceCodeSchema.safeParse(referenceCode).success;

  const canHandlePayment =
    !!selectedPaymentMethod &&
    !!invoice &&
    invoice.status?.toLowerCase() !== 'fullypaid' &&
    (partialPayment.enabled ? isValidPartialPayment : true);
  const handlePayment = async () => {
    if (!canHandlePayment || isProcessingPayment) return;
    setIsProcessingPayment(true);
    const amount = partialPayment.enabled
      ? partialPayment.amountNumberInCents
      : invoice.status?.toLowerCase() === 'unpaid'
        ? invoice.amountInclVAT
        : invoice.remainingAmountInclVAT;

    const paymentSessionBody = {
      referenceCode: invoice.referenceCode,
      deviceType,
      amount,
      paymentMethodId: selectedPaymentMethod.paymentMethodId,
      postPaymentRedirectionUrl: location.origin + '/pay/' + payCallbackPath,
      postPaymentRedirectionFailedUrl: location.origin + '/pay/error',
      postPaymentRedirectionCancelUrl: location.origin + '/pay/' + invoice.referenceCode
    };

    if (isSwishPayment) {
      // If a QR Code blob already exists, just open the modal
      if (
        qrCodeBlob &&
        !!paymentSession &&
        !failedPaymentStatuses.includes(paymentSession.payment.status?.toLowerCase())
      ) {
        setModalOpen(true);
        return;
      }

      postPaymentSession.mutate(paymentSessionBody, {
        onSuccess: async (result) => {
          const token = result.payment?.token;
          const redirection = result.payment?.redirection;
          const paymentSessionId = result.paymentSessionId;

          if (token && redirection) {
            vtlog('Swish token:', token);

            // Save token and redirection URL
            setSwishToken(token);
            setSwishRedirection(redirection);

            const qrBlob = await fetchQrCode.mutateAsync({ token });
            setQrCodeBlob(URL.createObjectURL(qrBlob));
            setModalOpen(true);
            setPaymentSessionId(paymentSessionId);
          } else {
            console.error('Token or redirection URL missing in payment session response');
          }
        },
        onError: (error) => {
          console.error('Error creating Swish payment session:', error);
          setIsProcessingPayment(false);
          if (error instanceof FetchError && error.status === 409) {
            refetchInvoice();
          } else {
            navigate('/pay/error', { replace: true });
          }
        },
        onSettled: async () => {
          setIsProcessingPayment(false);
        }
      });
    } else if (selectedPaymentMethod.paymentMethodId === bankTransferPaymentId) {
      navigate('/pay/confirmation', { replace: true });
      setIsProcessingPayment(false);
    } else if (isStripePayment) {
      if (paymentSession && pendingPaymentStatuses.includes(paymentSession.payment.status?.toLowerCase())) {
        try {
          await handleCancelPayment();
        } catch (error) {
          console.error('Error cancelling payment session:', error);
        }
      }

      postPaymentSession.mutate(paymentSessionBody, {
        onSuccess: (result) => {
          setStripeClientSecret(result.payment.clientSecret ?? null);
          setStripePublishableKey(result.payment.publishableKey ?? null);
          setStripePaymentSessionId(result.paymentSessionId);
          setPaymentSessionId(result.paymentSessionId);
          setStripeModalOpen(true);
        },
        onError: (error) => {
          console.error('Error creating Stripe payment session:', error);
          setIsProcessingPayment(false);
          if (error instanceof FetchError && error.status === 409) {
            refetchInvoice();
          } else {
            navigate('/pay/error', { replace: true });
          }
        },
        onSettled: () => setIsProcessingPayment(false)
      });
    } else {
      if (paymentSession && pendingPaymentStatuses.includes(paymentSession.payment.status?.toLowerCase())) {
        try {
          await handleCancelPayment();
        } catch (error) {
          console.error('Error cancelling payment session:', error);
        }
      }

      postPaymentSession.mutate(paymentSessionBody, {
        onSuccess: (result) => {
          window.location.href = result.payment.redirection;
        },
        onError: (error) => {
          console.error('Error creating payment session:', error);
          setIsProcessingPayment(false);
          if (error instanceof FetchError && error.status === 409) {
            refetchInvoice();
          } else {
            navigate('/pay/error', { replace: true });
          }
        }
      });
    }
  };

  const resetModalState = () => {
    setModalOpen(false);
    setShowQRCode(false);
    setIsProcessingPayment(false);
  };

  const handleShowQRCode = () => {
    setShowQRCode(true);
  };

  const handleGoToStartPage = async () => {
    await resetInvoice();
    navigate('/pay', { replace: true });
  };

  const resetSwishPayment = () => {
    resetModalState();
    setSwishToken(null);
    setSwishRedirection(null);
    setQrCodeBlob(null);
  };

  const handleCancelPayment = () => {
    return new Promise<void>((resolve, reject) => {
      const paymentSessionId = invoice?.paymentSessionId || paymentSession?.paymentSessionId;

      if (paymentSessionId) {
        cancelPaymentSession.mutate(paymentSessionId, {
          onSuccess: async (result) => {
            vtlog('Payment session cancelled:', result);
            setStripeModalOpen(false);
            resetPaymentSession();
            await refetchInvoice();
            resetSwishPayment();
            resolve();
          },
          onError: (error) => {
            console.error(error);
            navigate('/pay/error', { replace: true });
            reject(error);
          }
        });
      } else {
        resolve();
      }
    });
  };

  const validatePartialPaymentAmount = (value: string) => {
    const valueNumber = formatStringToAmountInCents(value);
    const result = partialPaymentAmountSchema.safeParse(valueNumber);

    if (!result.success) {
      setPartialPaymentError(
        t(result.error.errors[0].message, {
          minimumAmount: formatPriceString(selectedPaymentMethod?.minimumPaymentAmount || minimumPaymentAmount),
          amount: formatPriceString(invoice?.remainingAmountInclVAT || 0)
        })
      );
    } else {
      setPartialPaymentError('');
    }
  };

  const handleChangePaymentMethod = (paymentMethodId: number) => {
    setSelectedPaymentMethod(
      invoice?.paymentMethods.find((method) => method.paymentMethodId === paymentMethodId) || null
    );
  };

  const partialPaymentAmountSchema = createPartialPaymentSchema({
    minimumPaymentAmount: selectedPaymentMethod?.minimumPaymentAmount || minimumPaymentAmount,
    totalAmount: invoice?.remainingAmountInclVAT || 0,
    serviceProvider: selectedPaymentMethod?.serviceProvider
  });

  const handlePartialPaymentAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validatePartialPaymentAmount(e.target.value);
    partialPayment.setAmount(e.target.value);
  };

  useEffect(() => {
    if (referenceCodeFromParams) {
      const normalizedReferenceCode = referenceCodeFromParams.toUpperCase();
      if (isValidReferenceCode(normalizedReferenceCode)) {
        setInvoiceReferenceCode(normalizedReferenceCode);
      } else {
        navigate('/pay/error', { replace: true });
      }
    }
  }, [referenceCodeFromParams]);

  useEffect(() => {
    if (!invoice && queryError) {
      navigate('/pay/error', { replace: true });
    }

    if (invoice && invoice.status?.toLowerCase() === 'fullypaid') {
      navigate('/pay', { replace: true });
    }
  }, [invoice, queryError]);

  useEffect(() => {
    if (partialPayment.enabled && partialPayment.amount) {
      validatePartialPaymentAmount(partialPayment.amount);
    }
  }, [selectedPaymentMethod]);

  if (!invoice) {
    return <Loading height='50vh' />;
  }

  if (invoice?.paymentSessionId) {
    return (
      <Stack gap={4}>
        <Typography variant='body2' align='center'>
          {t('page.pay.payment-already-processing-info')}
        </Typography>
        <Stack alignItems='center' justifyContent='center' gap={4}>
          <Button fullWidth variant='contained' color='error' onClick={handleCancelPayment}>
            {t('page.invoice.cancel-payment')}
          </Button>
          <Button variant='text' color='primary' onClick={handleGoToStartPage}>
            {t('page.invoice.go-back-to-startpage')}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack justifyContent='center' alignItems='center' gap={4}>
      <Typography variant='body2' align='center'>
        {t('components.payment-option-selector.choose-pay-option')}
      </Typography>
      <ReferenceDetails invoice={invoice as Invoice} />

      <Stack gap={2} sx={{ width: '100%' }}>
        <Typography component='h2' variant='h2' fontWeight='bold'>
          {t('components.payment-option-selector.pay-option')}
        </Typography>
        <Paper>
          <PaymentOptionsSelector
            selectedPaymentMethod={selectedPaymentMethod}
            setSelectedPaymentMethod={handleChangePaymentMethod}
          />
        </Paper>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack sx={{ pb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems='center'
            justifyContent='space-between'
            gap={{ xs: 2, sm: 4 }}
          >
            <Box>
              <Typography variant='body2'>{t('components.payment-option-selector.remaining-amount')}</Typography>
              <Typography variant='body1' fontWeight='bold' align='center'>
                {invoice?.remainingAmountInclVAT && formatPriceString(invoice?.remainingAmountInclVAT)}
              </Typography>
            </Box>
            {invoice?.isSplitPaymentAllowed && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={partialPayment.enabled}
                      onChange={(e) => {
                        partialPayment.setEnabled(e.target.checked);
                        setTimeout(() => {
                          partialPayment.setAmount('');
                          setPartialPaymentError('');
                        }, 200);
                      }}
                    />
                  }
                  label={t('page.pay.pay-session.pay-partial-amount')}
                  labelPlacement='top'
                  sx={{
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    alignItems: 'center'
                  }}
                />
              </FormGroup>
            )}
          </Stack>
          <PartialPayment
            handlePartialPaymentAmountChange={handlePartialPaymentAmountChange}
            partialPaymentError={partialPaymentError}
            onEnter={(e) => e.key === 'Enter' && canHandlePayment && handlePayment()}
          />
        </Stack>
        <Stack alignItems='center'>
          <Button
            fullWidth
            variant='contained'
            onClick={handlePayment}
            disabled={!canHandlePayment || isProcessingPayment}
            color='primary'
          >
            {t('buttons.continue')}
          </Button>
        </Stack>
      </Paper>

      <Swish
        open={modalOpen}
        showQRCode={showQRCode}
        qrCodeBlob={qrCodeBlob}
        isProcessingPayment={isProcessingPayment}
        onConfirm={() => {
          if (swishToken && swishRedirection) {
            const swishUrl = getSwishAppUrl(swishToken, swishRedirection);
            if (swishUrl) {
              window.location.href = swishUrl;
            } else {
              vtlog('Swish URL could not be generated. Missing callback URL.');
            }
          }
        }}
        onConfirmOtherDevice={handleShowQRCode}
        onClose={resetModalState}
        onCancel={handleCancelPayment}
      />

      <StripePayment
        open={stripeModalOpen}
        clientSecret={stripeClientSecret}
        publishableKey={stripePublishableKey}
        returnUrl={
          stripePaymentSessionId
            ? `${location.origin}/pay/${payCallbackPath}?PaymentSessionId=${stripePaymentSessionId}`
            : null
        }
        onSuccess={() => navigate(`/pay/confirmation/${stripePaymentSessionId}`, { replace: true })}
        onClose={() => setStripeModalOpen(false)}
        onCancel={handleCancelPayment}
      />

      <Typography variant='body3' color='customGrey'>
        {t('page.pay.pay-session.payment-policy-confirmation')}{' '}
        <Link
          color='inherit'
          fontWeight='bold'
          variant='body3'
          href='https://akova.se/integritetspolicy'
          target='_blank'
        >
          {t('common-phrases.privacy-policy')}
        </Link>
      </Typography>
    </Stack>
  );
};

export default PaySession;
